import { memo, useEffect, useState } from 'react';

import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatClearIcon from '@mui/icons-material/FormatClear';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatStrikethroughIcon from '@mui/icons-material/FormatStrikethrough';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import SubscriptIcon from '@mui/icons-material/Subscript';
import SuperscriptIcon from '@mui/icons-material/Superscript';
import { Box, IconButton, Stack, styled } from '@mui/material';
import Subscript from '@tiptap/extension-subscript';
import Superscript from '@tiptap/extension-superscript';
import Underline from '@tiptap/extension-underline';
import { useEditor, EditorContent, Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';

const ToolBarButton = styled(IconButton)`
    border-radius: 0;

    &.is-active {
        color: ${({ theme }) => theme.palette.primary.main};
        background-color: ${({ theme }) => theme.palette.grey[200]};
    }
`;

ToolBarButton.defaultProps = {
    size: 'small',
};

const MenuBar = ({ editor }: { editor: Editor | null }): JSX.Element | null => {
    if (!editor) {
        return null;
    }

    return (
        <Stack
            direction="row"
            alignItems="center"
            sx={{
                borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
                borderRadius: '8px',
                overflow: 'hidden',
            }}
        >
            <ToolBarButton
                onClick={() => editor.chain().focus().toggleBold().run()}
                disabled={!editor.can().chain().focus().toggleBold().run()}
                className={editor.isActive('bold') ? 'is-active' : ''}
            >
                <FormatBoldIcon color="inherit" />
            </ToolBarButton>
            <ToolBarButton
                onClick={() => editor.chain().focus().toggleItalic().run()}
                disabled={!editor.can().chain().focus().toggleItalic().run()}
                className={editor.isActive('italic') ? 'is-active' : ''}
            >
                <FormatItalicIcon color="inherit" />
            </ToolBarButton>
            <ToolBarButton
                onClick={() => editor.chain().focus().toggleStrike().run()}
                disabled={!editor.can().chain().focus().toggleStrike().run()}
                className={editor.isActive('strike') ? 'is-active' : ''}
            >
                <FormatStrikethroughIcon color="inherit" />
            </ToolBarButton>
            <ToolBarButton
                onClick={() => editor.chain().focus().toggleUnderline().run()}
                disabled={!editor.can().chain().focus().toggleUnderline().run()}
                className={editor.isActive('underline') ? 'is-active' : ''}
            >
                <FormatUnderlinedIcon color="inherit" />
            </ToolBarButton>
            <ToolBarButton
                onClick={() =>
                    editor
                        .chain()
                        .focus()
                        .toggleSuperscript()
                        .unsetSubscript()
                        .run()
                }
                disabled={
                    !editor
                        .can()
                        .chain()
                        .focus()
                        .toggleSuperscript()
                        .unsetSubscript()
                        .run()
                }
                className={editor.isActive('superscript') ? 'is-active' : ''}
            >
                <SuperscriptIcon color="inherit" />
            </ToolBarButton>
            <ToolBarButton
                onClick={() =>
                    editor
                        .chain()
                        .focus()
                        .toggleSubscript()
                        .unsetSuperscript()
                        .run()
                }
                disabled={
                    !editor
                        .can()
                        .chain()
                        .focus()
                        .toggleSubscript()
                        .unsetSuperscript()
                        .run()
                }
                className={editor.isActive('subscript') ? 'is-active' : ''}
            >
                <SubscriptIcon color="inherit" />
            </ToolBarButton>
            <Box sx={{ flex: 1 }} />
            <ToolBarButton
                onClick={() =>
                    editor.chain().focus().unsetAllMarks().clearNodes().run()
                }
            >
                <FormatClearIcon color="inherit" sx={{ ml: 'auto' }} />
            </ToolBarButton>
        </Stack>
    );
};

type RichTextEditorProps = {
    value?: string;
    onChange?: (value: string) => void;
};

const EditorMemo = memo(EditorContent);

export const RichTextEditor = ({
    onChange = () => undefined,
    value = '',
}: RichTextEditorProps): JSX.Element => {
    const [focused, setFocused] = useState(false);

    const editor = useEditor(
        {
            extensions: [StarterKit, Underline, Superscript, Subscript],
            content: value,
            onUpdate: (e) => onChange(e.editor.getHTML()),
            editorProps: {
                attributes: {
                    class: 'rte-input',
                },
            },
            onBlur: () => setFocused(false),
            onFocus: () => setFocused(true),
        },
        []
    );

    useEffect(() => {
        if (!editor || editor.isDestroyed) return;
        const { selection } = editor.state;
        editor.commands.setContent(value, false, {
            preserveWhitespace: 'full',
        });
        editor.commands.setTextSelection(selection);
        editor.view.focus();
    }, [editor, value]);

    return (
        <Stack
            sx={{
                border: '1px solid',
                borderColor: 'rgba(0, 0, 0, 0.12)',
                borderRadius: '8px',
                boxShadow: focused ? '0 0 0 2px #0C6D36' : undefined,
                '.rte-input': {
                    padding: '8.5px 14px',
                    borderRadius: '8px',
                    outline: 'none',
                },
            }}
        >
            <MenuBar editor={editor} />
            <EditorMemo editor={editor} />
        </Stack>
    );
};
